import { Users } from 'lucide-react';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import { ContactCheckboxList } from '~/components/common/ContactCheckboxList';
import { SellerAccordionRow } from '~/components/common/SellerAccordionRow';
import { SellerRowHeader } from '~/components/common/SellerRowHeader';
import type { PickableSellerOrganization } from '~/components/common/SellerOrganizationPicker';
import { sellerLabel } from '~/utils';
import type { Contact, SavedContact } from '~/utils/contact';

export type SellerContactsRowLabels = {
  contactCount: (count: number) => string;
  noContacts: string;
  selectedCount: (count: number) => string;
  selectOrganization: (organizationLabel: string) => string;
};

export type SellerContactsRowRecipients = {
  selectedContactIds: Set<string>;
  isOrganizationSelected: (organizationId: string) => boolean;
  toggleOrganization: (organization: PickableSellerOrganization) => void;
  toggleContact: (
    organization: PickableSellerOrganization,
    contact: Contact
  ) => void;
  addContacts: (
    selections: {
      organization: PickableSellerOrganization;
      contacts: Contact[];
    }[]
  ) => void;
};

type SellerContactsRowProps = {
  organization: PickableSellerOrganization;
  recipients: SellerContactsRowRecipients;
  labels: SellerContactsRowLabels;
  testId: string;
  checkboxIdPrefix: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onContactAdded: (contact: SavedContact) => void;
  addContactDisabled?: boolean;
};

export function SellerContactsRow({
  organization,
  recipients,
  labels,
  testId,
  checkboxIdPrefix,
  expanded,
  onToggleExpand,
  onContactAdded,
  addContactDisabled = false,
}: Readonly<SellerContactsRowProps>) {
  const organizationLabel = sellerLabel(organization);
  const contacts = organization.sellerContacts;
  const hasContacts = contacts.length > 0;
  const organizationSelected = recipients.isOrganizationSelected(
    organization.id
  );
  const selectedCount = contacts.filter((contact) =>
    recipients.selectedContactIds.has(contact.id)
  ).length;

  const handleContactAdded = (contact: SavedContact) => {
    onContactAdded(contact);
    recipients.addContacts([{ organization, contacts: [contact] }]);
    if (!expanded) {
      onToggleExpand();
    }
  };

  return (
    <AddSellerContactPanel
      organizationId={organization.id}
      testId={`${testId}-add-contact`}
      disabled={addContactDisabled}
      onAdded={handleContactAdded}
    >
      {({ trigger, form }) => (
        <SellerAccordionRow
          testId={testId}
          disabled={!hasContacts}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
          highlighted={selectedCount > 0 || organizationSelected}
          leadingControl={
            <input
              type="checkbox"
              checked={organizationSelected}
              onChange={() => recipients.toggleOrganization(organization)}
              aria-label={labels.selectOrganization(organizationLabel)}
              data-testid={`${testId}-select`}
            />
          }
          trailingControl={trigger}
          belowHeader={
            form && (
              <div className="border-t border-neut-border bg-neut-bg-alt px-4 py-3">
                {form}
              </div>
            )
          }
          expandedContent={
            <ul className="space-y-0.5 border-t border-neut-border bg-neut-bg-alt px-4 py-2">
              <ContactCheckboxList
                contacts={contacts}
                selectedContactIds={recipients.selectedContactIds}
                onToggle={(contact) =>
                  recipients.toggleContact(organization, contact)
                }
                checkboxIdPrefix={checkboxIdPrefix}
                itemClassName="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-neut-50"
              />
            </ul>
          }
        >
          <SellerRowHeader
            organizationLabel={organizationLabel}
            subtitleIcon={Users}
            subtitle={
              hasContacts
                ? labels.contactCount(contacts.length)
                : labels.noContacts
            }
            selectedCountLabel={
              hasContacts && selectedCount > 0
                ? labels.selectedCount(selectedCount)
                : undefined
            }
          />
        </SellerAccordionRow>
      )}
    </AddSellerContactPanel>
  );
}
